import type {
  Counts,
  Discovery,
  ImportJob,
  ItemPage,
  Problem,
  StateChangeResult,
  SubscriptionSummary,
} from './types.js';

/**
 * The API client (M1.14.4).
 *
 * Three things it does that a bare `fetch` does not:
 *
 * 1. **Turns a problem document into a typed error.** Every failure from this
 *    API is RFC 9457, so the client parses it once and the screens render
 *    `problem.title` and `problem.detail` — rather than each caller inventing
 *    its own wording for a failure it cannot see.
 * 2. **Handles a 401 by refreshing, once.** Exactly once: a loop of refreshes
 *    against a revoked token is indistinguishable, from the outside, from an
 *    application that has hung.
 * 3. **Restarts pagination on a 409.** A cursor can be refused for four
 *    reasons and all of them mean the same thing to a reader — the list starts
 *    again. That is handled here so no screen has to know what a cursor is.
 *
 * Every request carries a correlation id. The server generates its own and
 * returns it in `x-correlation-id`; sending one as well means a tester's
 * report and a log line can be matched without either side guessing.
 */

/**
 * Every request header this client sends.
 *
 * Declared rather than implied, because the API's CORS configuration has to
 * allow each one and a browser refuses the whole request over a header it was
 * not told about. `tests/cors-contract.test.ts` asserts the two agree — the
 * drift is otherwise invisible until somebody clicks something.
 */
export const CLIENT_REQUEST_HEADERS = [
  'accept',
  'authorization',
  'content-type',
  'idempotency-key',
  'x-correlation-id',
] as const;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Problem,
    /** The server's correlation id, where one came back. */
    readonly correlationId?: string,
  ) {
    super(problem.title);
    this.name = 'ApiError';
  }

  /** What to put on screen. Never a stack trace, never a raw body. */
  get userMessage(): string {
    return this.problem.detail ?? this.problem.title;
  }
}

/** The backend is unreachable — a different thing from the backend saying no. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('The service could not be reached');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export interface ApiClientOptions {
  readonly baseUrl: string;
  /** Returns a valid ID token, refreshing if needed. */
  readonly getToken: () => Promise<string | undefined>;
  /** Forces a refresh after a 401. Returns the new token, or undefined. */
  readonly refreshToken: () => Promise<string | undefined>;
  /** Called when a request fails authentication twice: the session is over. */
  readonly onSignedOut: () => void;
  readonly fetchImpl?: typeof fetch;
  readonly newCorrelationId?: () => string;
}

export interface FolderSummary {
  readonly folderId: string;
  readonly name: string;
  readonly parentId?: string | null;
  readonly position?: number | null;
}

export interface TimelineQuery {
  readonly filter?: 'unread' | 'all';
  readonly folderId?: string;
  readonly feedId?: string;
  readonly cursor?: string;
  readonly limit?: number;
}

export interface TimelinePage extends ItemPage {
  /**
   * Set when the requested cursor was refused and the page was re-fetched
   * from the top.
   *
   * The caller needs to know, because it must discard what it had rather than
   * appending: the list it is holding was built from a cursor the server no
   * longer honours.
   */
  readonly restarted: boolean;
}

export class ApiClient {
  readonly #options: ApiClientOptions;
  readonly #fetch: typeof fetch;

  constructor(options: ApiClientOptions) {
    this.#options = options;
    this.#fetch = options.fetchImpl ?? fetch.bind(globalThis);
  }

  /* --------------------------------------------------------- endpoints --- */

  async counts(): Promise<Counts> {
    return this.#request<Counts>('GET', '/counts');
  }

  async subscriptions(): Promise<readonly SubscriptionSummary[]> {
    const body = await this.#request<{ subscriptions?: SubscriptionSummary[] }>(
      'GET',
      '/subscriptions',
    );
    return body.subscriptions ?? [];
  }

  /**
   * The folder tree.
   *
   * `parentId` and `position` come back too, and the sidebar needs both: it
   * renders the actual nesting, so a folder moved into another has to be
   * drawn inside it rather than beside it.
   */
  async folders(): Promise<readonly FolderSummary[]> {
    const body = await this.#request<{ folders?: FolderSummary[] }>('GET', '/folders');
    return body.folders ?? [];
  }

  async subscribe(url: string, folderId?: string): Promise<SubscriptionSummary> {
    return this.#request<SubscriptionSummary>('POST', '/subscriptions', {
      body: { url, ...(folderId === undefined ? {} : { folderId }) },
    });
  }

  /** The feeds on a page, without subscribing to any of them (M2.6.2). */
  async discover(url: string): Promise<Discovery> {
    return this.#request<Discovery>(
      'GET',
      `/subscriptions/discover?url=${encodeURIComponent(url)}`,
    );
  }

  /* ---- OPML import (M2.6.3) ---- */

  /**
   * Uploads a subscription list.
   *
   * Sent as JSON rather than as the file's bytes: a browser has read the file
   * into a string by this point, and `{ opml }` needs no special handling in
   * the client, in CORS, or in the API's body parsing.
   *
   * Small files come back finished; larger ones come back `running` with a
   * job id to poll. The caller does not have to care which — both are an
   * {@link ImportJob}, and a finished one simply has nothing left to wait for.
   */
  async importOpml(opml: string): Promise<ImportJob> {
    return this.#request<ImportJob>('POST', '/subscriptions/import', {
      body: { opml },
    });
  }

  /**
   * The account's subscriptions as an OPML file (M2.3.1).
   *
   * Returns text, not JSON — the one endpoint in this API that does. The
   * caller turns it into a download; this method exists so the request still
   * carries the token, the correlation id and the 401 refresh that every
   * other call gets.
   */
  async exportOpml(): Promise<string> {
    const token = await this.#options.getToken();
    const response = await this.#send('GET', '/subscriptions/export', token, {});

    if (response.status === 401) {
      const refreshed = await this.#options.refreshToken();
      if (refreshed === undefined) {
        this.#options.onSignedOut();
        throw new ApiError(401, {
          type: 'unauthenticated',
          title: 'Signed out',
          status: 401,
          detail: 'Your session has ended. Sign in again to continue.',
        });
      }
      const second = await this.#send('GET', '/subscriptions/export', refreshed, {});
      if (!second.ok) {
        throw new ApiError(second.status, {
          type: 'about:blank',
          title: 'The export could not be produced',
          status: second.status,
        });
      }
      return second.text();
    }

    if (!response.ok) {
      throw new ApiError(response.status, {
        type: 'about:blank',
        title: 'The export could not be produced',
        status: response.status,
      });
    }

    return response.text();
  }

  async importStatus(jobId: string): Promise<ImportJob> {
    return this.#request<ImportJob>(
      'GET',
      `/subscriptions/import/${encodeURIComponent(jobId)}`,
    );
  }

  /**
   * One page of the timeline, restarting from the top if the cursor is stale.
   *
   * The 409 is handled here rather than surfaced, because every screen would
   * otherwise write the same recovery — and a reader who scrolls for a minute
   * should see the list reload, not an error.
   */
  async timeline(query: TimelineQuery = {}): Promise<TimelinePage> {
    const search = new URLSearchParams();
    if (query.filter !== undefined) search.set('filter', query.filter);
    if (query.folderId !== undefined) search.set('folderId', query.folderId);
    if (query.feedId !== undefined) search.set('feedId', query.feedId);
    if (query.limit !== undefined) search.set('limit', String(query.limit));
    if (query.cursor !== undefined) search.set('cursor', query.cursor);

    try {
      const page = await this.#request<ItemPage>('GET', `/items?${search.toString()}`);
      return { ...page, restarted: false };
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 409) {
        throw error;
      }

      search.delete('cursor');
      const page = await this.#request<ItemPage>('GET', `/items?${search.toString()}`);
      return { ...page, restarted: true };
    }
  }

  async markRead(itemId: string, read: boolean): Promise<StateChangeResult> {
    return this.#request<StateChangeResult>(
      read ? 'PUT' : 'DELETE',
      `/items/${encodeURIComponent(itemId)}/read`,
    );
  }

  async markAllRead(
    target: { subscriptionId: string; upToSeq?: number } | { folderId: string },
    idempotencyKey?: string,
  ): Promise<StateChangeResult> {
    return this.#request<StateChangeResult>('POST', '/items/read/bulk', {
      body: target,
      ...(idempotencyKey === undefined ? {} : { idempotencyKey }),
    });
  }

  /* ---- folders and subscriptions (M2.1.1, M2.5.1) ---- */

  async createFolder(
    name: string,
    parentId?: string,
  ): Promise<{ folderId: string; name: string }> {
    return this.#request('POST', '/folders', {
      body: { name, ...(parentId === undefined ? {} : { parentId }) },
    });
  }

  /**
   * Renames a folder, moves it, or both (M2.6.1).
   *
   * `parentId: null` means the top level and an absent `parentId` means
   * unchanged — the same distinction the API draws, kept here rather than
   * flattened, because a rename that also un-nested the folder would be a
   * surprising thing for a rename to do.
   */
  async updateFolder(
    folderId: string,
    changes: { name?: string; parentId?: string | null; position?: number },
  ): Promise<void> {
    await this.#request('PATCH', `/folders/${encodeURIComponent(folderId)}`, {
      body: changes,
    });
  }

  async renameFolder(folderId: string, name: string): Promise<void> {
    await this.updateFolder(folderId, { name });
  }

  /** Nests a folder inside another, or moves it to the top with `null`. */
  async moveFolder(folderId: string, parentId: string | null): Promise<void> {
    await this.updateFolder(folderId, { parentId });
  }

  /** Subscriptions inside it move to `reassignTo`, or to no folder. */
  async deleteFolder(folderId: string, reassignTo?: string): Promise<void> {
    const query =
      reassignTo === undefined ? '' : `?reassignTo=${encodeURIComponent(reassignTo)}`;
    await this.#request('DELETE', `/folders/${encodeURIComponent(folderId)}${query}`);
  }

  async updateSubscription(
    subscriptionId: string,
    changes: {
      titleOverride?: string | null;
      folderId?: string | null;
      position?: number;
    },
  ): Promise<SubscriptionSummary> {
    return this.#request<SubscriptionSummary>(
      'PATCH',
      `/subscriptions/${encodeURIComponent(subscriptionId)}`,
      { body: changes },
    );
  }

  async unsubscribe(subscriptionId: string): Promise<void> {
    await this.#request('DELETE', `/subscriptions/${encodeURIComponent(subscriptionId)}`);
  }

  /**
   * Instrumentation (M1.16.3, M1.17.2, M1.17.3, M1.18.2).
   *
   * Failures are swallowed on purpose: a reader must never see an error
   * because a metric could not be recorded.
   */
  async reportEvents(
    events: readonly { name: string; properties?: Record<string, unknown> }[],
  ): Promise<void> {
    if (events.length === 0) {
      return;
    }
    try {
      await this.#request('POST', '/events', { body: { events }, keepalive: true });
    } catch {
      // Deliberately ignored.
    }
  }

  /* ----------------------------------------------------------- plumbing --- */

  async #request<T>(
    method: string,
    path: string,
    options: { body?: unknown; idempotencyKey?: string; keepalive?: boolean } = {},
  ): Promise<T> {
    const token = await this.#options.getToken();
    const first = await this.#send(method, path, token, options);

    if (first.status !== 401) {
      return this.#parse<T>(first);
    }

    /*
     * One refresh, then give up. A token can be a minute out of date because
     * a laptop slept; it can also be revoked, and retrying that forever is a
     * hang rather than an error.
     */
    const refreshed = await this.#options.refreshToken();

    if (refreshed === undefined) {
      this.#options.onSignedOut();
      throw new ApiError(401, {
        type: 'unauthenticated',
        title: 'Signed out',
        status: 401,
        detail: 'Your session has ended. Sign in again to continue.',
      });
    }

    const second = await this.#send(method, path, refreshed, options);

    if (second.status === 401) {
      this.#options.onSignedOut();
    }

    return this.#parse<T>(second);
  }

  async #send(
    method: string,
    path: string,
    token: string | undefined,
    options: { body?: unknown; idempotencyKey?: string; keepalive?: boolean },
  ): Promise<Response> {
    const correlationId = (this.#options.newCorrelationId ?? defaultCorrelationId)();

    try {
      return await this.#fetch(`${this.#options.baseUrl}${path}`, {
        method,
        headers: {
          accept: 'application/json',
          'x-correlation-id': correlationId,
          ...(token === undefined ? {} : { authorization: `Bearer ${token}` }),
          ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
          ...(options.idempotencyKey === undefined
            ? {}
            : { 'idempotency-key': options.idempotencyKey }),
        },
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
        /*
         * `keepalive` for the telemetry flush: the last events of a session
         * are sent as the page is being torn down, and an ordinary request is
         * cancelled with it.
         */
        ...(options.keepalive === true ? { keepalive: true } : {}),
      });
    } catch (error) {
      /*
       * `fetch` rejects for a dropped connection, a DNS failure, and a CORS
       * refusal alike — which at alpha most often means the app stack has
       * been torn down. The screens say so rather than showing a spinner.
       */
      throw new NetworkError(error);
    }
  }

  async #parse<T>(response: Response): Promise<T> {
    if (response.status === 204) {
      // No body by design, from `DELETE` and from `/events`.
      return undefined as T;
    }

    if (response.status === 202) {
      /*
       * A 202 may or may not carry one. `/events` sends nothing; an accepted
       * import sends the job to poll, and reading that as "done, nothing to
       * say" would lose the job id and leave the screen with nothing to wait
       * on. So: parse it if it is there.
       */
      const text = await response.text();
      return (text.trim() === '' ? undefined : JSON.parse(text)) as T;
    }

    if (response.ok) {
      return (await response.json()) as T;
    }

    const correlationId = response.headers.get('x-correlation-id') ?? undefined;

    let problem: Problem;
    try {
      problem = (await response.json()) as Problem;
    } catch {
      // A failure that is not a problem document — a gateway error, usually.
      problem = {
        type: 'about:blank',
        title: 'Something went wrong',
        status: response.status,
      };
    }

    throw new ApiError(
      response.status,
      problem,
      ...(correlationId === undefined ? [] : [correlationId]),
    );
  }
}

const defaultCorrelationId = (): string =>
  globalThis.crypto?.randomUUID?.() ??
  `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
